/**
 * Doctor Profiles API client. Plain typed async fns over the `api` singleton +
 * buildQueryParams. Precedent: lib/hr-api.ts (list/upsert) + lib/documents-api.ts
 * (FormData multipart photo upload). `api` already prefixes /api/v1.
 *
 * All routes sit behind the `doctor_profiles_enabled` feature flag (they 404
 * when the flag is off). The self endpoints (`/me/doctor-profile`) additionally
 * 404 for callers without the doctor role.
 */

import api from "./api"
import { buildQueryParams } from "./utils/query-params"
import type { ApiResponse, PaginatedResponse } from "@/types"
import type {
  Doctor,
  DoctorFilters,
  DoctorProfile,
  UpsertDoctorProfileData,
  UpdateOwnDoctorProfileData,
} from "./doctor-types"

// ── Admin directory + profiles ──────────────────────────────────────────────

/**
 * GET /doctors — paginated directory of doctor-role users (each with its
 * optional profile). Mirrors hr-api.getStaff: returns the full paginated
 * envelope (`.data` = Doctor[], `.meta` = page info).
 */
export const getDoctors = async (filters: DoctorFilters = {}) => {
  const query = buildQueryParams({ ...filters })
  const response = await api.get<PaginatedResponse<Doctor>>(
    `/doctors?${query}`,
  )
  return response.data
}

/** GET /doctors/{userId} — a single doctor + nested profile (house envelope). */
export const getDoctor = async (userId: number): Promise<Doctor> => {
  const response = await api.get<ApiResponse<Doctor>>(`/doctors/${userId}`)
  return response.data.data
}

/**
 * PUT /doctors/{userId} — admin upsert of the full profile (updateOrCreate,
 * lazy creation). Callers refetch the directory / doctor afterwards.
 */
export const upsertDoctorProfile = async (
  userId: number,
  data: UpsertDoctorProfileData,
): Promise<void> => {
  await api.put(`/doctors/${userId}`, data)
}

/**
 * POST /doctors/{userId}/photo — replace the doctor's photo (MediaLibrary
 * singleFile). Returns the updated profile so the UI can show the new
 * photo_url/photo_thumb_url immediately. Field name `photo` matches the backend
 * UploadDoctorPhotoRequest + the `photo` collection (panoramic/selfie precedent).
 */
export const uploadDoctorPhoto = async (
  userId: number,
  file: File,
): Promise<DoctorProfile> => {
  const formData = new FormData()
  formData.append("photo", file)
  const response = await api.post<ApiResponse<DoctorProfile>>(
    `/doctors/${userId}/photo`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  )
  return response.data.data
}

/** DELETE /doctors/{userId}/photo — clear the doctor's photo. */
export const deleteDoctorPhoto = async (userId: number): Promise<void> => {
  await api.delete(`/doctors/${userId}/photo`)
}

// ── Self-service (doctor's own profile) ─────────────────────────────────────

/** GET /me/doctor-profile — the caller's own profile (financials visible). */
export const getMyDoctorProfile = async (): Promise<DoctorProfile> => {
  const response = await api.get<ApiResponse<DoctorProfile>>(
    `/me/doctor-profile`,
  )
  return response.data.data
}

/**
 * PUT /me/doctor-profile — doctor self-edits their OWN identity fields only
 * (scheduling/financials are ignored server-side). Callers refetch afterwards.
 */
export const updateMyDoctorProfile = async (
  data: UpdateOwnDoctorProfileData,
): Promise<void> => {
  await api.put(`/me/doctor-profile`, data)
}

/**
 * POST /me/doctor-profile/photo — replace the caller's own photo. Returns the
 * updated profile (new photo_url/photo_thumb_url) for immediate UI feedback.
 */
export const uploadMyDoctorPhoto = async (
  file: File,
): Promise<DoctorProfile> => {
  const formData = new FormData()
  formData.append("photo", file)
  const response = await api.post<ApiResponse<DoctorProfile>>(
    `/me/doctor-profile/photo`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  )
  return response.data.data
}
